import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import type { LinkCandidate, LinkRequestRefusal, LinkRequestSent } from '../lib/link-candidates';
import { untilLabel } from '../lib/time';
import { Button } from './ui/Button';

// One "Other sign-ins with your email" row (#7426 decision 2). Presenter:
// `<LinkCandidatesPanel/>` owns the send and hands down where this row stands.
// Every listed candidate is linkable (the API lists only SSO-only users of the one
// org the person shares), so the row names that org and offers the send.

/** Where one row's send stands. `refused.message` is the API's own detail for `other`. */
export type LinkRowState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent'; readonly sent: LinkRequestSent }
  | { readonly kind: 'refused'; readonly refusal: LinkRequestRefusal; readonly message: string };

/** The sentence for a refused send: the page's own words, or the API's detail. */
function refusalText(refusal: LinkRequestRefusal, message: string): string {
  switch (refusal.kind) {
    case 'not_a_candidate':
      return t('logins.candidate_not_a_candidate');
    case 'rate_limited': {
      if (refusal.retryAfterSeconds === null) return t('logins.candidate_rate_limited_later');
      const left = untilLabel(
        new Date(Date.now() + refusal.retryAfterSeconds * 1000).toISOString(),
      );
      return refusal.per === 'address'
        ? t('logins.candidate_rate_limited_address', { left })
        : t('logins.candidate_rate_limited', { left });
    }
    case 'send_failed':
      return t('logins.candidate_send_failed');
    case 'browser_session_required':
      return t('logins.candidate_browser_session_required');
    case 'other':
      return message;
  }
}

export function LinkCandidateRow(props: {
  readonly candidate: LinkCandidate;
  readonly state: LinkRowState;
  readonly onSend: () => void;
}): JSX.Element {
  return (
    <li class="link-candidate">
      <div class="link-candidate__who">
        <p class="link-candidate__login">{props.candidate.login}</p>
        <p class="link-candidate__facts">
          {`${t(`logins.provider_${props.candidate.provider}`)} · ${t('logins.candidate_through', { orgs: props.candidate.org })}`}
        </p>
        <p class="link-candidate__email">{props.candidate.email}</p>
      </div>
      <div class="link-candidate__action">
        <Switch>
          <Match when={props.state.kind === 'sent' && props.state}>
            {(s) => (
              <p class="link-candidate__note" role="status">
                {t('logins.candidate_sent', {
                  email: s().sent.sentTo,
                  left: untilLabel(s().sent.expiresAt),
                })}
              </p>
            )}
          </Match>
          <Match when={true}>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('logins.candidate_send_label', { login: props.candidate.login })}
              disabled={props.state.kind === 'sending'}
              onClick={() => props.onSend()}
            >
              {props.state.kind === 'sending'
                ? t('logins.candidate_sending')
                : t('logins.candidate_send')}
            </Button>
            <Show when={props.state.kind === 'refused' && props.state}>
              {(s) => (
                <p class="link-candidate__note link-candidate__note--refused" role="alert">
                  {refusalText(s().refusal, s().message)}
                </p>
              )}
            </Show>
          </Match>
        </Switch>
      </div>
    </li>
  );
}
